/**
 * Product → brand presentation. Pure and DI-free (typed `string` rather than the data
 * layer's `ProductType` so `util` keeps no dependency on `data` — same precedent as
 * `academy-poster`), so every academy layer can share one brand mapping instead of
 * re-declaring a per-product ternary per page.
 *
 * Taxonomy: FacilFit=1, Actuar=2, Ediz=3, Toletus=4, Tickou=5. Content with no product (code 0) is unlabeled — handled as the
 * fallback branch here.
 */

/**
 * Subset of `@shared/components/ui/badge`'s `TBadgeType` used for product tones.
 * Declared locally (not imported) so this `type:util` lib keeps zero dependency on the
 * `type:ui` design kit — Sheriff forbids `util → ui`. Every value below is a real
 * `lib-badge` type, so consumers bind it straight to `[type]` with no cast.
 */
export type ProductBadgeTone = 'orange' | 'blue' | 'cyan' | 'emerald' | 'pink';

/**
 * Per-product brand presentation: display label (proper nouns — not translated), a
 * `lib-badge` tone, and the app's `--primary` glow hex.
 *
 * Badge tones map each product to the closest available `lib-badge` type:
 * FácilFit → orange, Actuar → blue, Ediz → cyan,
 * Toletus → emerald (teal placeholder — no toletus palette exists in the repo),
 * Tickou → pink. Distinct tones so the products read apart in a filter bar.
 */
interface ProductBrand {
  readonly label: string;
  readonly badge: ProductBadgeTone;
  readonly glow: string;
  /** flat-uicons token (short name, no `fi-rr-` prefix) for product pickers/toggles. */
  readonly icon: string;
}

const PRODUCT_BRANDS: Readonly<Record<string, ProductBrand>> = {
  FacilFit: {
    label: 'FácilFit',
    badge: 'orange',
    glow: '#F97316',
    icon: 'apps',
  },
  Actuar: {
    label: 'Actuar',
    badge: 'blue',
    glow: '#4043DD',
    icon: 'chart-histogram',
  },
  Ediz: { label: 'Ediz', badge: 'cyan', glow: '#11C2E0', icon: 'book-alt' },
  // Toletus glow is a PLACEHOLDER teal — no toletus palette exists in the repo yet.
  Toletus: {
    label: 'Toletus',
    badge: 'emerald',
    glow: '#0D9488',
    icon: 'cube',
  },
  Tickou: { label: 'Tickou', badge: 'pink', glow: '#f41e6b', icon: 'ticket' },
};

/** Fallback for unlabeled content (code 0) or any unknown product. */
const FALLBACK_BRAND: ProductBrand = PRODUCT_BRANDS['FacilFit'];

/** Product → badge tone (a valid `lib-badge` type). */
export function productBadge(product: string): ProductBadgeTone {
  return (PRODUCT_BRANDS[product] ?? FALLBACK_BRAND).badge;
}

/** Product → display brand label (proper nouns, not translated). */
export function productLabel(product: string): string {
  return (PRODUCT_BRANDS[product] ?? FALLBACK_BRAND).label;
}

/** Product → brand glow hex (each app's `--primary`) for poster/glow sites. */
export function productGlow(product: string): string {
  return (PRODUCT_BRANDS[product] ?? FALLBACK_BRAND).glow;
}

/**
 * One selectable product for the builder product pickers (Detalhes field + the
 * by-product audience reflection): the `ProductType` NAME, its brand label, and a
 * flat-uicons token. `key` stays typed `string` so `util` keeps no dependency on
 * the `data` layer's `ProductType` (same precedent as the rest of this file).
 */
export interface AcademyProductPickerOption {
  readonly key: string;
  readonly label: string;
  readonly icon: string;
}

/**
 * Single source of truth for the product list offered by every academy builder
 * (course / path / live). Ordered by the wire taxonomy (FacilFit=1, Actuar=2,
 * Ediz=3, Toletus=4, Tickou=5) and derived from the brand table, so a new product
 * lights up every picker at once instead of drifting across three hardcoded arrays.
 */
export const ACADEMY_PRODUCT_PICKER_OPTIONS: readonly AcademyProductPickerOption[] =
  (['FacilFit', 'Actuar', 'Ediz', 'Toletus', 'Tickou'] as const).map((key) => ({
    key,
    label: PRODUCT_BRANDS[key].label,
    icon: PRODUCT_BRANDS[key].icon,
  }));
